import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Menu,
    X,
    ArrowRight,
    Zap,
    ShieldCheck,
    LineChart,
    CheckCircle2,
    FileText,
    Sparkles,
    BarChart3,
    Clock,
    Activity
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';

const Home = () => {
    const navigate = useNavigate();
    const { signUp } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Contact Form State
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [contactLoading, setContactLoading] = useState(false);
    const [contactSuccess, setContactSuccess] = useState(false);
    const [contactError, setContactError] = useState('');

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setContactError('');
        setContactLoading(true);

        try {
            const botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
            const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;

            if (botToken && chatId) {
                const text = `🚨 *New Enquiry from FR-BillNet!*\n\n*Name:* ${contactForm.name}\n*Email:* ${contactForm.email}\n*Phone:* ${contactForm.phone}\n*Message:* ${contactForm.message}`;
                
                const response = await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        chat_id: chatId,
                        text: text,
                        parse_mode: 'Markdown'
                    })
                });

                if (!response.ok) {
                    throw new Error('Failed to send to Telegram');
                }
            } else {
                console.warn('Telegram Bot credentials not configured in .env');
                // Fallback simulation if no keys
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            setContactSuccess(true);
            setContactForm({ name: '', email: '', phone: '', message: '' });
        } catch (error) {
            console.error('Telegram Error:', error);
            setContactError('Failed to send message. Please try again.');
        } finally {
            setContactLoading(false);
        }
    };

    const navLinks = [
        { name: 'Home', href: '#home' },
        { name: 'Features', href: '#features' },
        { name: 'About Us', href: '#about' },
        { name: 'Contact Us', href: '#contact' }
    ];

    return (
        <div className="min-h-screen bg-yellow-50 font-sans text-slate-900 overflow-x-hidden scroll-smooth relative">

            {/* Decorative Top Background Pattern */}
            <div className="absolute top-0 inset-x-0 h-[600px] bg-gradient-to-b from-yellow-100/50 via-white to-yellow-50/10 pointer-events-none"></div>

            {/* Premium Navbar */}
            <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200/50 py-4 px-6 md:px-12 flex justify-between items-center shadow-sm transition-all">
                {/* Indigo Accent Line */}
                <div className="absolute top-0 left-0 w-full h-[3px] bg-indigo-600"></div>

                {/* Logo */}
                <div
                    className="flex items-center cursor-pointer group"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <span className="text-2xl font-black tracking-tight text-indigo-900 transition-colors group-hover:text-indigo-700">FR-Bill</span>
                    <span className="text-2xl font-black tracking-tight text-orange-500 transition-colors group-hover:text-orange-600">Net</span>
                </div>

                {/* Desktop Nav */}
                <div className="hidden md:flex items-center space-x-10">
                    {navLinks.map(link => (
                        <a
                            key={link.name}
                            href={link.href}
                            className="text-sm font-bold text-slate-600 hover:text-indigo-600 transition-colors"
                        >
                            {link.name}
                        </a>
                    ))}
                </div>

                {/* Desktop Auth Buttons */}
                <div className="hidden md:flex items-center gap-6">
                    <button
                        onClick={() => navigate('/login')}
                        className="text-sm font-bold text-indigo-900 hover:text-indigo-600 transition-colors"
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() => navigate('/signup')}
                        className="bg-indigo-600 text-white text-sm font-bold rounded-lg px-6 py-2.5 hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg hover:-translate-y-0.5 transform duration-200"
                    >
                        Get Started
                    </button>
                </div>

                {/* Mobile Menu Toggle */}
                <div className="md:hidden">
                    <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-indigo-900 p-2">
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-40 bg-white pt-24 px-6 md:hidden animate-in slide-in-from-top-4 duration-300">
                    <div className="space-y-6 flex flex-col pb-8 border-b border-slate-100">
                        {navLinks.map(link => (
                            <a
                                key={link.name}
                                onClick={() => setMobileMenuOpen(false)}
                                href={link.href}
                                className="text-xl font-bold text-slate-800 border-b border-slate-50 pb-4 hover:text-indigo-600 transition-colors"
                            >
                                {link.name}
                            </a>
                        ))}
                        <button
                            onClick={() => {
                                setMobileMenuOpen(false);
                                navigate('/login');
                            }}
                            className="w-full py-4 text-center border-2 border-indigo-600 text-indigo-600 font-bold rounded-lg mt-4"
                        >
                            Sign In
                        </button>
                        <button
                            onClick={() => {
                                setMobileMenuOpen(false);
                                navigate('/signup');
                            }}
                            className="w-full py-4 text-center bg-indigo-600 text-white font-bold rounded-lg shadow-sm"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            )}

            {/* Hero Section with Mock UI */}
            <section id="home" className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
                <div className="container mx-auto px-6 md:px-12 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center gap-16">

                        {/* Hero Text Content */}
                        <div className="w-full lg:w-1/2 text-center lg:text-left animate-in fade-in slide-in-from-bottom-8 duration-700">

                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-indigo-700 font-bold text-xs uppercase tracking-widest mb-8 shadow-sm">
                                <Sparkles className="h-4 w-4 text-orange-500" /> Premium Billing Platform
                            </div>

                            <h1 className="text-4xl md:text-6xl lg:text-[4.5rem] font-black text-slate-900 leading-[1.1] tracking-tight mb-6">
                                Effortless Invoicing.<br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-800">Powerful Control.</span>
                            </h1>

                            <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                                Streamline your franchise operations with a platform designed for speed and accuracy. Manage billing, compliance, and growth—all from a single, beautiful dashboard.
                            </p>

                            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                                <button 
                                    onClick={() => navigate('/signup')}
                                    className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-orange-500 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl hover:bg-orange-600 hover:-translate-y-0.5 transition-all"
                                >
                                    Create Free Account <ArrowRight className="h-5 w-5" />
                                </button>
                                <a
                                    href="#features"
                                    className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-white border border-slate-200 hover:border-indigo-200 text-slate-700 hover:text-indigo-700 font-bold px-8 py-4 rounded-xl shadow-sm hover:shadow-md transition-all"
                                >
                                    Explore Features
                                </a>
                            </div>
                        </div>

                        {/* Floating Mock Dashboard Graphics */}
                        <div className="w-full lg:w-1/2 hidden lg:block animate-in fade-in zoom-in-95 duration-1000 delay-300 fill-mode-both">
                            <div className="relative w-full max-w-lg mx-auto">
                                {/* Decorative Blobs behind UI */}
                                <div className="absolute -top-10 -right-10 w-64 h-64 bg-orange-200 rounded-full blur-[60px] opacity-60 pointer-events-none"></div>
                                <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-200 rounded-full blur-[60px] opacity-60 pointer-events-none"></div>

                                {/* Main App Window */}
                                <div className="relative bg-white rounded-2xl shadow-[0_20px_50px_rgba(30,27,75,0.08)] border border-slate-100 overflow-hidden transform hover:-translate-y-2 transition-transform duration-700">
                                    {/* App Header */}
                                    <div className="h-12 bg-yellow-50 border-b border-slate-100 flex items-center px-4 gap-2">
                                        <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                                        <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                                        <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                                    </div>
                                    {/* App Content */}
                                    <div className="p-6">
                                        <div className="mb-6">
                                            <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1">Platform Status</div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-3xl font-black text-indigo-950">Active Engine</div>
                                                <div className="px-2 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full uppercase flex items-center gap-1 animate-pulse">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div> Online
                                                </div>
                                            </div>
                                        </div>

                                        {/* Capabilities List */}
                                        <div className="space-y-4 mb-6">
                                            {[
                                                { title: "Smart AWB Validation", desc: "Prevents duplicate entry errors", icon: ShieldCheck, color: "text-indigo-500", bg: "bg-indigo-50" },
                                                { title: "Real-time GST Split", desc: "Calculates taxable freight automatically", icon: Zap, color: "text-orange-500", bg: "bg-orange-50" },
                                                { title: "Automated Invoicing", desc: "Generates instant PDF ledgers", icon: FileText, color: "text-blue-500", bg: "bg-blue-50" },
                                            ].map((item, i) => (
                                                <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:border-indigo-100 hover:shadow-sm transition-all group">
                                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${item.bg} ${item.color} group-hover:scale-110 transition-transform`}>
                                                        <item.icon className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-slate-800">{item.title}</div>
                                                        <div className="text-xs text-slate-500 font-medium">{item.desc}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Progress bar mock */}
                                        <div className="p-4 bg-yellow-50 rounded-lg border border-slate-100">
                                            <div className="flex justify-between text-xs font-bold text-slate-500 mb-2 uppercase">
                                                <span>System Load</span>
                                                <span className="text-indigo-600">Optimal</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500 w-[24%] rounded-full relative">
                                                    <div className="absolute inset-0 bg-white/20 animate-[pulse_2s_ease-in-out_infinite]"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Floating Element Overlay */}
                                <div className="absolute -bottom-8 -left-12 bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(249,115,22,0.15)] border border-orange-100 animate-bounce" style={{ animationDuration: '4s' }}>
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                                            <Zap className="h-6 w-6 text-orange-500" />
                                        </div>
                                        <div>
                                            <div className="text-xs font-bold text-slate-400 uppercase">Fast Action</div>
                                            <div className="text-sm font-black text-slate-800">Docket Processed</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-24 bg-white border-t border-slate-100">
                <div className="container mx-auto px-6 md:px-12">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-indigo-600 font-bold tracking-widest uppercase text-xs mb-3 flex items-center justify-center gap-3">
                            <span className="h-px w-8 bg-indigo-600"></span>
                            Why Choose FR-BillNet
                            <span className="h-px w-8 bg-indigo-600"></span>
                        </h2>
                        <h3 className="text-3xl md:text-5xl font-black text-slate-900 mb-6">Everything you need to manage your franchise</h3>
                        <p className="text-slate-600 text-lg">We simplified the complexity of administration so you can focus on growing your business with absolute financial clarity.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                title: "Quick Billing",
                                desc: "Instantly process consignments. The system automatically calculates weight and surcharges for you.",
                                icon: Zap,
                                color: "text-orange-500",
                                iconBg: "bg-orange-50",
                                border: "border-orange-100"
                            },
                            {
                                title: "Automated Taxes",
                                desc: "Easily separate taxable and non-taxable revenue, making your GST filing a breeze.",
                                icon: ShieldCheck,
                                color: "text-indigo-600",
                                iconBg: "bg-indigo-50",
                                border: "border-indigo-100"
                            },
                            {
                                title: "Live Reports",
                                desc: "Track every earning and deduction in real-time. Know exactly where your finances stand.",
                                icon: LineChart,
                                color: "text-emerald-500",
                                iconBg: "bg-emerald-50",
                                border: "border-emerald-100"
                            },
                            {
                                title: "Instant PDFs",
                                desc: "Generate and download professional PDF invoices in seconds to share with your clients.",
                                icon: FileText,
                                color: "text-blue-500",
                                iconBg: "bg-blue-50",
                                border: "border-blue-100"
                            }
                        ].map((feature, index) => (
                            <div
                                key={index}
                                className="group relative bg-white p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_40px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${feature.iconBg} ${feature.color} border ${feature.border} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    <feature.icon className="h-7 w-7" />
                                </div>
                                <h4 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h4>
                                <p className="text-slate-600 leading-relaxed text-sm font-medium">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>            {/* Contact Section */}
            <section id="contact" className="py-24 relative overflow-hidden bg-yellow-50/50">
                <div className="container mx-auto px-6 md:px-12 relative z-10">
                    <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-[0_20px_60px_rgba(30,27,75,0.05)] border border-slate-100 overflow-hidden flex flex-col lg:flex-row relative">

                        {/* Elegant Decorative Line */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-orange-500"></div>

                        {/* Left Side: Context */}
                        <div className="lg:w-5/12 p-10 lg:p-14 bg-indigo-950 text-white flex flex-col justify-center relative overflow-hidden">
                            {/* Subtle Background Pattern inside dark box */}
                            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:20px_20px]"></div>

                            <h3 className="text-3xl md:text-4xl font-black mb-6 relative z-10 leading-tight">Get in touch with our team</h3>
                            <p className="text-indigo-200 text-lg mb-8 relative z-10 font-medium leading-relaxed">Have questions about FR-BillNet? We're here to help you optimize your franchise operations.</p>

                            <div className="space-y-6 relative z-10">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-900 flex items-center justify-center border border-indigo-700 mr-4">
                                        <Clock className="h-5 w-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-white">Fast Response</h5>
                                        <p className="text-indigo-300 text-sm mt-1">We aim to respond to all inquiries within 2 hours.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-900 flex items-center justify-center border border-indigo-700 mr-4">
                                        <ShieldCheck className="h-5 w-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-white">Dedicated Support</h5>
                                        <p className="text-indigo-300 text-sm mt-1">Speak directly with our onboarding specialists.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Form */}
                        <div className="lg:w-7/12 p-10 lg:p-14">
                            {contactSuccess ? (
                                <div className="text-center py-10 animate-in zoom-in-95 duration-500">
                                    <div className="h-20 w-20 bg-emerald-50 text-emerald-500 border border-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle2 className="h-10 w-10" />
                                    </div>
                                    <h4 className="text-3xl font-black text-slate-900 mb-4">Message Sent!</h4>
                                    <p className="text-slate-600 mb-8 font-medium">Thank you for reaching out. A member of our team will get back to you shortly.</p>
                                    <button
                                        onClick={() => setContactSuccess(false)}
                                        className="inline-flex justify-center items-center bg-indigo-600 text-white font-bold px-8 py-4 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm hover:shadow-md"
                                    >
                                        Send Another Message <ArrowRight className="h-5 w-5 ml-2" />
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <h4 className="text-2xl font-black text-slate-900 mb-8">Send us a message</h4>
                                    <form onSubmit={handleContactSubmit} className="space-y-5">
                                        {contactError && (
                                            <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm font-bold rounded-lg flex items-start gap-2">
                                                <X className="h-5 w-5 shrink-0" />
                                                <span>{contactError}</span>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Your Name *</label>
                                                <input
                                                    required
                                                    type="text"
                                                    value={contactForm.name}
                                                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                                    className="w-full border border-slate-200 rounded-xl p-3.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-yellow-50 focus:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Phone Number</label>
                                                <input
                                                    type="tel"
                                                    value={contactForm.phone}
                                                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                                    className="w-full border border-slate-200 rounded-xl p-3.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-yellow-50 focus:bg-white"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Email Address *</label>
                                            <input
                                                required
                                                type="email"
                                                value={contactForm.email}
                                                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                                className="w-full border border-slate-200 rounded-xl p-3.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-yellow-50 focus:bg-white"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Message *</label>
                                            <textarea
                                                required
                                                rows="4"
                                                value={contactForm.message}
                                                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                                className="w-full border border-slate-200 rounded-xl p-3.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-yellow-50 focus:bg-white resize-none"
                                            ></textarea>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={contactLoading}
                                            className="w-full mt-4 py-4 bg-orange-500 text-white font-black uppercase tracking-widest text-sm rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                        >
                                            {contactLoading ? 'Sending...' : 'Send Message'}
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Premium Minimal Footer */}
            <footer className="bg-white py-12 border-t border-slate-200">
                <div className="container mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex flex-col items-center md:items-start gap-1">
                        <div className="flex items-center">
                            <span className="text-2xl font-black tracking-tight text-indigo-900">FR-Bill</span>
                            <span className="text-2xl font-black tracking-tight text-orange-500">Net</span>
                        </div>
                    </div>

                    <p className="text-slate-400 text-sm font-medium">
                        &copy; {new Date().getFullYear()} FR-BillNet Framework. All rights reserved.
                    </p>

                    <div className="flex space-x-6">
                        <a href="#home" className="text-slate-400 hover:text-indigo-600 transition-colors text-sm font-bold tracking-wide">Home</a>
                        <a href="#features" className="text-slate-400 hover:text-indigo-600 transition-colors text-sm font-bold tracking-wide">Features</a>
                        <a href="#register" className="text-slate-400 hover:text-indigo-600 transition-colors text-sm font-bold tracking-wide">Sign Up</a>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default Home;
